
import { Vue, Component, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import {
  getDocumentUrlFromId,
  replaceDocumentWithAnother
} from "@/lib/wordpress/load";
import EditTemplateModal from "@/subapps/manage-templates/EditTemplateModal.vue";
import {
  SupportedPostType,
  Template,
  TemplatesManagementData
} from "@/subapps/manage-templates/types";
import { DocumentMeta } from "@/types/document";
import { configStore, documentStore } from "@/store/store";
import { pipe } from "fp-ts/function";
import { getAllTemplatesAndNeededDataFromWordPress } from "@/subapps/manage-templates/lib/api";
import { fold } from "fp-ts/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import { getBreakdanceBlockPostType } from "@/lib/constants/post-types";
import EditGlobalBlockModal from "@/subapps/manage-templates/EditGlobalBlockModal.vue";
import { GlobalBlock } from "@/types/global-blocks";
import { getBlocksAndPutIntoStore } from "@/lib/global-blocks/global-blocks";
import { decodeHtmlEntities } from "@/util/dom";

@Component({
  components: { EditGlobalBlockModal, EditTemplateModal, Icon }
})
export default class DocumentChooserItem extends Vue {
  @Prop()
  item!: DocumentMeta;

  @Prop({ default: false })
  highlight!: boolean;

  templates: Template[] = [];

  get globalBlocks() {
    return configStore.globalBlocks;
  }

  modalData: { type: "edit" | "add"; data: Template | GlobalBlock } | false =
    false;
  loadingData = false;

  templatesManagementData: TemplatesManagementData = {
    conditions: [],
    dynamicFields: [],
    templateTypeCategories: [],
    popupTriggers: null
  };

  get title() {
    if (this.item.titleLabel) {
      return decodeHtmlEntities(this.item.titleLabel);
    }

    return `ID: ${this.item.id}`;
  }

  get currentDocument() {
    return documentStore.document;
  }

  get templateParentsWithoutTrashed() {
    return [];
  }

  get templatesByStatus() {
    return [];
  }

  get documentUrl() {
    return getDocumentUrlFromId(this.item.id);
  }

  get isBreakdancePostType() {
    return (
      this.item.postType.startsWith(`${configStore.builderMode}_`) &&
      !this.item.postType.endsWith(`${configStore.builderMode}_part`)
    );
  }

  get currentTemplate() {
    return this.templates.find(t => t.id === this.item.id);
  }

  get currentGlobalBlock() {
    return this.globalBlocks.find(t => t.id === this.item.id);
  }

  get isGlobalBlockTemplate() {
    return this.item.postType === getBreakdanceBlockPostType();
  }

  async editGlobalBlock() {
    this.loadingData = true;

    await this.loadGlobalBlocks();

    if (!this.currentGlobalBlock) {
      NotificationLogger.log.message("Couldn't load Global Blocks data");
      return;
    }

    this.modalData = {
      type: "edit",
      data: this.currentGlobalBlock
    };

    this.loadingData = false;
  }

  async editTemplate() {
    this.loadingData = true;

    await this.loadTemplates();

    if (!this.currentTemplate) {
      NotificationLogger.log.message("Couldn't load templates data");
      return;
    }

    this.modalData = {
      type: "edit",
      data: this.currentTemplate
    };

    this.loadingData = false;
  }

  async loadGlobalBlocks() {
    return getBlocksAndPutIntoStore();
  }

  async loadTemplates() {
    const type = this.item.postType as SupportedPostType;

    pipe(
      await getAllTemplatesAndNeededDataFromWordPress(type),
      fold(
        () => {
          NotificationLogger.log.message("Couldn't load templates data");
        },
        data => {
          this.templates = data.templates;
          this.templatesManagementData = data.templatesManagementData;
          configStore.setDynamicFields(
            data.templatesManagementData.dynamicFields
          );
          configStore.setConditions(data.templatesManagementData.conditions);
          configStore.setEnvironment(data.environment);
        }
      )
    );
  }

  async replaceCurrentBuilderPage(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) return;
    if (window.Breakdance.initialMode !== "builder") return;

    event.preventDefault();
    this.$emit("click");

    if (this.item.id === documentStore.document.documentMeta.id) return;

    replaceDocumentWithAnother(this.item.id);
  }
}
